// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { doAwait, doFinally } from "@nevware21/ts-async";
import { arrSlice, fnApply, isFunction, isObject, isPromiseLike } from "@nevware21/ts-utils";
import { _DYN_CONCAT, _DYN_CONFIG, _DYN_HOST, _DYN_INITIALIZE, _DYN_MESSAGE, _DYN_SET_ACTIVE_SPAN, _DYN_SPAN_CONTEXT, _DYN_SPAN_ID, _DYN_SUPPRESS_TRACING, _DYN_TRACE_CFG, _DYN_TRACE_ID } from "../../../__DynamicConstants";
import { createDistributedTraceContext, isDistributedTraceContext } from "../../../core/TelemetryHelpers";
import { isValidSpanId, isValidTraceId } from "../../../utils/TraceParent";
import { createSpan } from "./span";
/**
 * Internal helper to execute a callback function with a span set as the active span.
 * Handles both synchronous and asynchronous (Promise-based) callbacks, ensuring
 * the previous active span is properly restored after execution.
 * @param scope - The span scope instance
 * @param fn - The callback function to execute
 * @param thisArg - The `this` context for the callback
 * @param args - Array of arguments to pass to the callback
 * @returns The result of the callback function
 */
function _executeWithActiveSpan(scope, fn, thisArg, args) {
    var isAsync = false;
    try {
        var result = fnApply(fn, thisArg || scope, args);
        if (isPromiseLike(result)) {
            isAsync = true;
            return doFinally(result, function () {
                // Restore previous active span after promise settles (resolves or rejects)
                if (scope) {
                    scope.restore();
                }
            });
        }
        return result;
    }
    finally {
        // Restore previous active span only if result is not a promise
        // (promises handle restoration in their callbacks)
        if (scope && !isAsync) {
            scope.restore();
        }
    }
}
/**
 * Execute the callback `fn` function with the passed span as the active span
 * Note: The callback will be executed even if the span is null.
 * @param traceHost - The current trace host instance (core or AISKU instance)
 * @param span - The span to set as the active span during the execution of the callback
 * @param fn - the callback function
 * @param thisArg - the `this` argument for the callback. If not provided, ISpanScope is used as `this`
 * @returns the result of the function
 */
export function withSpan(traceHost, span, fn, thisArg) {
    var scope = traceHost[_DYN_SET_ACTIVE_SPAN /* @min:%2esetActiveSpan */](span);
    return _executeWithActiveSpan(scope, fn, thisArg, arrSlice(arguments, 4));
}
/**
 * Execute the callback `fn` function with the passed span as the active span. The callback receives
 * an ISpanScope object as its first parameter and the `this` context (when no thisArg is provided).
 * Note: The callback will be executed even if the span is null.
 * @param traceHost - The current trace host instance (core or AISKU instance)
 * @param span - The span to set as the active span during the execution of the callback
 * @param fn - the callback function that receives an ISpanScope and additional arguments
 * @param thisArg - the `this` argument for the callback. If not provided, ISpanScope is used as `this`
 * @param _args - Additional arguments to be passed to the function
 */
export function useSpan(traceHost, span, fn, thisArg) {
    var scope = traceHost[_DYN_SET_ACTIVE_SPAN /* @min:%2esetActiveSpan */](span);
    return _executeWithActiveSpan(scope, fn, thisArg, [scope][_DYN_CONCAT /* @min:%2econcat */](arrSlice(arguments, 4)));
}
/**
 * Creates and starts a new span, sets it as the active span in the current context,
 * and executes a provided function within this context.
 *
 * This method creates a span, makes it active during the execution of the provided
 * function, and automatically ends the span when the function completes (or throws).
 * This provides automatic span lifecycle management and context propagation. If the function
 * is asynchronous the span will be ended when the returned Promise resolves or rejects.
 * Note: The callback will be executed even if the traceHost is unable to create a span (returns null).
 * @remarks
 * This overloaded version supports both optional span creation options and the `this` argument for the callback.
 * @param name - The name of the span, should be descriptive of the operation being traced
 * @param optionsOrFn - Optional configuration for span creation (parent context, attributes, etc.) or the function to execute within the span's active context
 * @param maybeFnOrThis - The function to execute within the span's active context or the `this` argument for the callback
 * @param thisArg - The `this` argument for the callback. If not provided, ISpanScope is used as `this`
 * @returns The result of executing the provided function
 * @remarks
 * - The span is automatically ended when the function completes or throws an exception
 * - The span becomes the active parent for any spans created within the function
 * - If the function throws an error, the span status is automatically set to ERROR
 * - This is the recommended method for most tracing scenarios due to automatic lifecycle management
 * - Multiple overloads available for different parameter combinations
 */
export function startActiveSpan(traceHost, name, optionsOrFn, maybeFnOrThis, thisArg) {
    var options = null;
    var fn;
    var that = thisArg;
    if (isFunction(optionsOrFn)) {
        fn = optionsOrFn;
    }
    else {
        options = optionsOrFn;
        fn = maybeFnOrThis;
        that = thisArg || maybeFnOrThis;
    }
    var span = traceHost.startSpan(name, options);
    var useAsync = false;
    try {
        var result = useSpan(traceHost, span, function (scope) {
            return fnApply(fn, that, [scope]);
        });
        if (isPromiseLike(result)) {
            useAsync = true;
            return doAwait(result, function (value) {
                if (span) {
                    span.end();
                }
                return value;
            }, function (reason) {
                if (span) {
                    span.setStatus({ code: reason ? 2 /* eOTelSpanStatusCode.ERROR */ : 1 /* eOTelSpanStatusCode.OK */, message: reason ? reason[_DYN_MESSAGE /* @min:%2emessage */] || reason : undefined });
                    span.end();
                }
                // Re-throw the rejection so the returned promise rejects rather than
                // silently resolving with undefined (see issue #2749).
                throw reason;
            });
        }
        return result;
    }
    catch (e) {
        if (span) {
            span.setStatus({ code: e ? 2 /* eOTelSpanStatusCode.ERROR */ : 1 /* eOTelSpanStatusCode.OK */, message: e ? e[_DYN_MESSAGE /* @min:%2emessage */] : undefined });
        }
        throw e;
    }
    finally {
        // If the function returned a promise, we need to end the span when the promise resolves/rejects
        if (!useAsync && span) {
            span.end();
        }
    }
}
/**
 * Returns true if the passed spanContext of type  {@link IDistributedTraceContext} or {@link IDistributedTraceInit} is valid.
 * @return true if this {@link IDistributedTraceContext} is valid.
 */
/*#__NO_SIDE_EFFECTS__*/
export function isSpanContextValid(spanContext) {
    return spanContext ? (isValidTraceId(spanContext[_DYN_TRACE_ID /* @min:%2etraceId */]) && isValidSpanId(spanContext[_DYN_SPAN_ID /* @min:%2espanId */])) : false;
}
/**
 * Wrap the given {@link IDistributedTraceContext} in a new non-recording {@link IReadableSpan}
 *
 * @param spanContext - span context to be wrapped
 * @returns a new non-recording {@link IReadableSpan} with the provided context
 */
export function wrapSpanContext(otelApi, spanContext) {
    if (!isDistributedTraceContext(spanContext)) {
        spanContext = createDistributedTraceContext(spanContext);
    }
    // Return a non-recording span
    return createNonRecordingSpan(otelApi, "wrapped(\"" + spanContext[_DYN_SPAN_ID /* @min:%2espanId */] + "\")", spanContext);
}
/**
 * Return a non-recording span based on the provided spanContext using the otelApi instance as the
 * owning instance.
 * @param otelApi - The otelApi to use for creating the non-Recording Span
 * @param spanName - The span name to associated with the span
 * @param spanContext - The Span context to use for the span
 * @returns A new span that is marked as a non-recording span
 */
export function createNonRecordingSpan(otelApi, spanName, spanContext) {
    // Return a non-recording span
    var spanCtx = {
        api: otelApi,
        spanContext: isDistributedTraceContext(spanContext) ? spanContext : createDistributedTraceContext(spanContext),
        isRecording: false
    };
    return createSpan(spanCtx, spanName, 0 /* eOTelSpanKind.INTERNAL */);
}
/**
 * Identifies whether the span is an {@link IReadableSpan} or not
 * @param span - The span to check
 * @returns true if the span is an {@link IReadableSpan} otherwise false
 */
/*#__NO_SIDE_EFFECTS__*/
export function isReadableSpan(span) {
    return !!span &&
        isObject(span) &&
        "name" in span &&
        "kind" in span &&
        isFunction(span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]) &&
        "duration" in span &&
        "ended" in span &&
        "startTime" in span &&
        "endTime" in span &&
        "attributes" in span &&
        "links" in span &&
        "events" in span &&
        "status" in span &&
        // "resource" in span &&
        // "instrumentationScope" in span &&
        "droppedAttributesCount" in span &&
        isFunction(span.isRecording) &&
        isFunction(span.setStatus) &&
        isFunction(span.updateName) &&
        isFunction(span.setAttribute) &&
        isFunction(span.setAttributes) &&
        isFunction(span.end) &&
        isFunction(span.recordException);
}
function _getTraceCfg(context) {
    var traceCfg = null;
    if (context) {
        if (context.cfg && context[_DYN_HOST /* @min:%2ehost */]) {
            traceCfg = context.cfg[_DYN_TRACE_CFG /* @min:%2etraceCfg */];
        }
        else if (isFunction(context[_DYN_INITIALIZE /* @min:%2einitialize */]) && context[_DYN_CONFIG /* @min:%2econfig */]) {
            traceCfg = context[_DYN_CONFIG /* @min:%2econfig */][_DYN_TRACE_CFG /* @min:%2etraceCfg */];
        }
        else if (context[_DYN_TRACE_CFG /* @min:%2etraceCfg */]) {
            traceCfg = context[_DYN_TRACE_CFG /* @min:%2etraceCfg */];
        }
    }
    return traceCfg;
}
/**
 * Set the suppress tracing flag on the context
 * @param context - The context to set the suppress tracing flag on
 * @returns The context with the suppress tracing flag set
 * @remarks This is used to suppress tracing for the current context and all child contexts
 */
/*#__NO_SIDE_EFFECTS__*/
export function suppressTracing(context) {
    var traceCfg = _getTraceCfg(context);
    if (traceCfg) {
        traceCfg[_DYN_SUPPRESS_TRACING /* @min:%2esuppressTracing */] = true;
    }
    return context;
}
/**
 * Remove the suppress tracing flag from the context
 * @param context - The context to remove the suppress tracing flag from
 * @returns The context with the suppress tracing flag removed
 * @remarks This is used to remove the suppress tracing flag from the current context and all child
 * contexts. This is used to restore tracing after it has been suppressed
 */
/*#__NO_SIDE_EFFECTS__*/
export function unsuppressTracing(context) {
    var traceCfg = _getTraceCfg(context);
    if (traceCfg) {
        traceCfg[_DYN_SUPPRESS_TRACING /* @min:%2esuppressTracing */] = false;
    }
    return context;
}
/**
 * Check if the tracing is suppressed for the current context
 * @param context - The context to check for the suppress tracing flag
 * @returns true if tracing is suppressed for the current context otherwise false
 * @remarks This is used to check if tracing is suppressed for the current context and all child
 */
/*#__NO_SIDE_EFFECTS__*/
export function isTracingSuppressed(context) {
    var result = false;
    var traceCfg = _getTraceCfg(context);
    if (traceCfg) {
        result = !!traceCfg[_DYN_SUPPRESS_TRACING /* @min:%2esuppressTracing */];
    }
    return result;
}
//# sourceMappingURL=utils.js.map